import React, { ReactNode, useState } from 'react'

export type CheckboxProps = React.InputHTMLAttributes<HTMLInputElement> & {
  label: ReactNode
  labelProps?: React.HTMLAttributes<HTMLSpanElement>
  variant?: 'small' | 'medium' | 'big'
  glyphProps?: React.SVGProps<SVGSVGElement>
}

const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      label,
      labelProps,
      checked: controlledChecked,
      onChange,
      children,
      variant = 'small',
      glyphProps,
      ...props
    },
    ref
  ) => {
    const [uncontrolledChecked, setUncontrolledChecked] = useState(false)

    const isControlled = controlledChecked !== undefined
    const isChecked = isControlled ? controlledChecked : uncontrolledChecked

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newChecked = e.target.checked

      if (!isControlled) {
        setUncontrolledChecked(newChecked)
      }

      if (onChange) {
        onChange({
          ...e,
          target: {
            ...e.target,
            value: newChecked.toString(),
          },
        })
      }
    }

    const Glyph = isChecked ? <SelectedNormal /> : <UnselectedNormal />
    const sizeClass =
      variant === 'small'
        ? 'w-4 h-4'
        : variant === 'medium'
          ? 'w-5 h-5'
          : 'w-8 h-8'

    return (
      <label
        {...labelProps}
        className={`relative flex flex-row items-center gap-2 font-sans font-medium appearance-none ${props.disabled ? 'cursor-not-allowed' : 'cursor-pointer'} md:items-center ${labelProps?.className ?? ''}`}
      >
        <div className="flex relative flex-1 gap-2 items-center">
          <input
            ref={ref}
            {...props}
            type="checkbox"
            checked={isChecked}
            onChange={handleChange}
            className="appearance-none !bg-none !absolute inset-0 w-full h-full !border-0 !border-none ring-0 !bg-transparent shadow-none active:outline-none focus:outline-none focus-visible:outline-none active:border-none focus:border-none focus-visible:border-none focus:shadow-none focus:[box-shadow:none]"
          />
          {React.cloneElement(Glyph, {
            ...glyphProps,
            className: `${sizeClass} shrink-0 ${props.disabled ? 'opacity-50' : ''} ${glyphProps?.className ?? ''}`,
          })}
        </div>
        <span className={`inline-flex flex-col leading-[120%] w-full`}>
          {label}
        </span>
        {children}
      </label>
    )
  }
)

Checkbox.displayName = 'Checkbox'

export { Checkbox }

const UnselectedNormal = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 16 16"
    fill="none"
    {...props}
  >
    <rect width={15} height={15} x={0.5} y={0.5} fill="transparent" rx={3.5} />
    <rect
      width={15}
      height={15}
      x={0.5}
      y={0.5}
      stroke="currentColor"
      rx={3.5}
    />
  </svg>
)

const SelectedNormal = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 16 16"
    fill="none"
    {...props}
  >
    <rect width={15} height={15} x={0.5} y={0.5} fill="#292929" rx={3.5} />
    <rect width={15} height={15} x={0.5} y={0.5} stroke="#292929" rx={3.5} />
    <path
      stroke="#fff"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeMiterlimit={10}
      d="m3.5 8 3 3 6-6"
    />
  </svg>
)
